/**
 * @name ThemeToggleIconButton
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Botão moderno para alternar entre temas claro e escuro com animações suaves inspiradas no tema de clube de praia
 * @param {ThemeToggleIconButtonProps} props - As props para o componente.
 * @returns {JSX.Element} - O JSX do componente.
 * @package src/components/buttons/ThemeToggleIconButton.tsx
 **/

import NightlightIcon from '@mui/icons-material/Nightlight';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { Box, IconButton, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import useThemeContext from '../../../themes/hooks/useThemeContext';

interface ThemeToggleIconButtonProps {
     color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
     size?: 'small' | 'medium' | 'large';
     edge?: 'start' | 'end' | false;
}

const ThemeToggleIconButton: React.FC<ThemeToggleIconButtonProps> = ({ color = 'default', size = 'medium', edge = false }) => {
     const { theme, toggleTheme } = useThemeContext();
     const isDarkMode = theme.palette.mode === 'dark';
     const [isAnimating, setIsAnimating] = useState(false);

     const handleToggle = () => {
          setIsAnimating(true);
          setTimeout(() => {
               toggleTheme();
               setTimeout(() => setIsAnimating(false), 300);
          }, 150);
     };

     const tooltipText = isDarkMode ? 'Tema Claro' : 'Tema Escuro';

     return (
          <Tooltip
               title={tooltipText}
               arrow
               placement='top'
          >
               <Box
                    sx={{
                         position: 'relative',
                         display: 'inline-flex',
                         borderRadius: '50%',
                         overflow: 'hidden',
                    }}
               >
                    <IconButton
                         onClick={handleToggle}
                         color={color}
                         size={size}
                         edge={edge}
                         disabled={isAnimating}
                         sx={{
                              position: 'relative',
                              background: isDarkMode ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 50%, #5c6bc0 100%)' : 'linear-gradient(135deg, #ffb74d 0%, #ff9800 50%, #f57c00 100%)',
                              color: '#ffffff',
                              borderRadius: '50%',
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: isAnimating ? 'scale(1.1) rotate(180deg)' : 'scale(1) rotate(0deg)',
                              boxShadow: isDarkMode ? '0 4px 20px rgba(57, 73, 171, 0.4), 0 0 0 2px rgba(92, 107, 192, 0.2)' : '0 4px 20px rgba(255, 152, 0, 0.4), 0 0 0 2px rgba(245, 124, 0, 0.2)',
                              '&:hover': {
                                   transform: isAnimating ? 'scale(1.1) rotate(180deg)' : 'scale(1.05) rotate(5deg)',
                                   boxShadow: isDarkMode ? '0 6px 25px rgba(57, 73, 171, 0.6), 0 0 0 3px rgba(92, 107, 192, 0.3)' : '0 6px 25px rgba(255, 152, 0, 0.6), 0 0 0 3px rgba(245, 124, 0, 0.3)',
                              },
                              '&:active': {
                                   transform: 'scale(0.95)',
                              },
                              '&::before': {
                                   content: '""',
                                   position: 'absolute',
                                   top: 0,
                                   left: 0,
                                   right: 0,
                                   bottom: 0,
                                   background: isDarkMode ? 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)' : 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.4) 0%, transparent 50%)',
                                   borderRadius: '50%',
                                   opacity: 1,
                                   transition: 'opacity 0.3s ease',
                              },
                              '&:hover::before': {
                                   opacity: 0.8,
                              },
                         }}
                    >
                         <Box
                              sx={{
                                   position: 'relative',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   transform: isAnimating ? 'rotate(-180deg) scale(1.2)' : 'rotate(0deg) scale(1)',
                                   transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                   zIndex: 1,
                              }}
                         >
                              {isDarkMode ? (
                                   <WbSunnyIcon
                                        sx={{
                                             fontSize: size === 'small' ? '1.2rem' : size === 'large' ? '1.8rem' : '1.5rem',
                                             filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))',
                                        }}
                                   />
                              ) : (
                                   <NightlightIcon
                                        sx={{
                                             fontSize: size === 'small' ? '1.2rem' : size === 'large' ? '1.8rem' : '1.5rem',
                                             filter: 'drop-shadow(0 0 6px rgba(26, 35, 126, 0.4))',
                                        }}
                                   />
                              )}
                         </Box>
                    </IconButton>
               </Box>
          </Tooltip>
     );
};

export default ThemeToggleIconButton;
